/**
 * Phân vùng giao hàng cho bảng giá 3 vùng của ShopTrack.
 *
 * Nguồn:
 * - Định nghĩa vùng và ranh giới miền lấy từ trang bảng giá chính thức của
 *   BEST (best-inc.vn/services/express/pricing, trích ngày 2026-09-18, xem
 *   packages/common/src/data/best-express-pricing.json):
 *     · Nội thành: Hà Nội – Hà Nội, Hồ Chí Minh – Hồ Chí Minh
 *     · Nội tỉnh:  Tỉnh A – Tỉnh A
 *     · Nội miền:  cùng miền (Bắc – Bắc, Trung – Trung, Nam – Nam)
 *     · Liên miền: khác miền
 *     · Miền Bắc: từ Ninh Bình trở ra. Miền Trung: từ Thanh Hóa trở vào Bình
 *       Thuận. Miền Nam: từ Đồng Nai trở vào.
 * - Địa giới: 34 tỉnh/thành sau sáp nhập (hiệu lực 01/07/2025). API BEST nhận
 *   địa chỉ 2 cấp nên tên tỉnh cũ được quy về tỉnh mới trước khi so.
 *
 * Bảng giá ShopTrack có 4 cột (docs/KE_HOACH_COT_LIEN_TINH.md):
 *   1. Nội tỉnh  — cùng tỉnh mới. Gồm luôn "Nội thành" của BEST (HN–HN,
 *                  HCM–HCM) vì bảng giá ShopTrack không có cột Nội thành.
 *   2. Liên tỉnh — khác tỉnh, cùng miền, hai tỉnh GIÁP RANH (PROVINCE_NEIGHBORS).
 *                  BEST không có vùng này — đơn giáp ranh BEST vẫn tính Nội miền,
 *                  nên giá gốc Liên tỉnh admin nhập phải bằng số BEST thực thu.
 *   3. Nội miền  — khác tỉnh, cùng miền, không giáp ranh.
 *   4. Liên miền — khác miền (kể cả hai tỉnh giáp ranh nằm ở hai miền).
 * Không nhận ra tỉnh gửi hoặc tỉnh nhận → trả `null`; nơi gọi tự quyết (không
 * lặng lẽ coi là Nội tỉnh — mức giá rẻ nhất).
 */

import { findProvince, removeVietnameseAccents, cleanAdminPrefix } from './vietnamAddressResolver.js'

export type VietnamRegion = 'bac' | 'trung' | 'nam'
export { type ShippingRoute, SHIPPING_ROUTES, ROUTE_LABELS } from '../routes.js'
import type { ShippingRoute } from '../routes.js'
import { SHIPPING_ROUTES, ROUTE_LABELS } from '../routes.js'

/** Một câu mô tả điều kiện của từng vùng — hiện cho người dùng ở màn bảng giá / tạo đơn. */
export const ROUTE_RULES: Record<ShippingRoute, string> = {
  noi_tinh: 'Tỉnh gửi và tỉnh nhận là cùng một tỉnh/thành (theo địa giới 34 tỉnh).',
  lien_tinh: 'Khác tỉnh, cùng miền, hai tỉnh giáp ranh nhau.',
  noi_mien: 'Khác tỉnh, cùng miền, không giáp ranh.',
  lien_mien: 'Khác miền (Bắc – Trung – Nam).',
}

/** Ranh giới miền theo BEST, ghi lại để hiện cho người dùng. */
export const REGION_RULES: Record<VietnamRegion, string> = {
  bac: 'Miền Bắc: từ Ninh Bình trở ra',
  trung: 'Miền Trung: từ Thanh Hóa trở vào Lâm Đồng (gồm Bình Thuận cũ)',
  nam: 'Miền Nam: từ Đồng Nai trở vào',
}

/** 34 tỉnh/thành sau sáp nhập và miền theo BEST. Tên ở dạng rút gọn (không "Tỉnh"/"Thành phố"). */
export const PROVINCE_REGION: Record<string, VietnamRegion> = {
  // Miền Bắc — từ Ninh Bình trở ra
  'Hà Nội': 'bac',
  'Cao Bằng': 'bac',
  'Tuyên Quang': 'bac',
  'Lào Cai': 'bac',
  'Điện Biên': 'bac',
  'Lai Châu': 'bac',
  'Sơn La': 'bac',
  'Thái Nguyên': 'bac',
  'Lạng Sơn': 'bac',
  'Quảng Ninh': 'bac',
  'Phú Thọ': 'bac',
  'Bắc Ninh': 'bac',
  'Hải Phòng': 'bac',
  'Hưng Yên': 'bac',
  'Ninh Bình': 'bac',
  // Miền Trung — từ Thanh Hóa trở vào Bình Thuận cũ (nay thuộc Lâm Đồng), gồm Tây Nguyên
  'Thanh Hóa': 'trung',
  'Nghệ An': 'trung',
  'Hà Tĩnh': 'trung',
  'Quảng Trị': 'trung',
  'Huế': 'trung',
  'Đà Nẵng': 'trung',
  'Quảng Ngãi': 'trung',
  'Gia Lai': 'trung',
  'Đắk Lắk': 'trung',
  'Khánh Hòa': 'trung',
  'Lâm Đồng': 'trung',
  // Miền Nam — từ Đồng Nai trở vào
  'Hồ Chí Minh': 'nam',
  'Đồng Nai': 'nam',
  'Tây Ninh': 'nam',
  'Đồng Tháp': 'nam',
  'Vĩnh Long': 'nam',
  'Cần Thơ': 'nam',
  'An Giang': 'nam',
  'Cà Mau': 'nam',
}

/**
 * Cặp tỉnh GIÁP RANH trên đất liền theo địa giới 34 tỉnh (hiệu lực 01/07/2025) —
 * dựng từ ranh giới giữa 63 tỉnh cũ rồi quy về tỉnh mới (MERGED_INTO), bỏ ranh
 * giới biển. Dữ liệu tính tiền (vùng Liên tỉnh): sửa phải đối chiếu bản đồ hành
 * chính và ghi vào nhật ký docs/KE_HOACH_COT_LIEN_TINH.md.
 * Cặp khác miền (đánh dấu ↔) vẫn có trong danh sách cho đủ dữ liệu, nhưng
 * resolveShippingRoute xét miền trước nên tính Liên miền.
 */
export const PROVINCE_NEIGHBORS: readonly (readonly [string, string])[] = [
  // ── Miền Bắc ──
  ['Lai Châu', 'Điện Biên'],
  ['Lai Châu', 'Sơn La'],
  ['Lai Châu', 'Lào Cai'],
  ['Điện Biên', 'Sơn La'],
  ['Sơn La', 'Lào Cai'], // Yên Bái cũ
  ['Sơn La', 'Phú Thọ'], // Phú Thọ, Hòa Bình cũ
  ['Lào Cai', 'Tuyên Quang'], // Lào Cai – Hà Giang, Yên Bái – Tuyên Quang
  ['Lào Cai', 'Phú Thọ'], // Yên Bái – Phú Thọ
  ['Tuyên Quang', 'Cao Bằng'],
  ['Tuyên Quang', 'Thái Nguyên'], // Tuyên Quang – Thái Nguyên, Bắc Kạn
  ['Tuyên Quang', 'Phú Thọ'], // Tuyên Quang – Phú Thọ, Vĩnh Phúc
  ['Cao Bằng', 'Thái Nguyên'], // Bắc Kạn cũ
  ['Cao Bằng', 'Lạng Sơn'],
  ['Thái Nguyên', 'Lạng Sơn'],
  ['Thái Nguyên', 'Bắc Ninh'], // Bắc Giang cũ
  ['Thái Nguyên', 'Hà Nội'],
  ['Thái Nguyên', 'Phú Thọ'], // Vĩnh Phúc cũ
  ['Lạng Sơn', 'Bắc Ninh'], // Bắc Giang cũ
  ['Lạng Sơn', 'Quảng Ninh'],
  ['Quảng Ninh', 'Bắc Ninh'], // Bắc Giang cũ
  ['Quảng Ninh', 'Hải Phòng'], // Hải Phòng, Hải Dương cũ
  ['Bắc Ninh', 'Hà Nội'],
  ['Bắc Ninh', 'Hưng Yên'],
  ['Bắc Ninh', 'Hải Phòng'], // Bắc Ninh, Bắc Giang – Hải Dương
  ['Hà Nội', 'Phú Thọ'], // Phú Thọ, Vĩnh Phúc, Hòa Bình cũ
  ['Hà Nội', 'Hưng Yên'],
  ['Hà Nội', 'Ninh Bình'], // Hà Nam cũ
  ['Hưng Yên', 'Hải Phòng'], // Hải Dương, Thái Bình – Hải Phòng
  ['Hưng Yên', 'Ninh Bình'], // Hưng Yên – Hà Nam, Thái Bình – Nam Định
  ['Phú Thọ', 'Ninh Bình'], // Hòa Bình – Hà Nam, Ninh Bình
  // ── Bắc ↔ Trung ──
  ['Sơn La', 'Thanh Hóa'],
  ['Phú Thọ', 'Thanh Hóa'], // Hòa Bình cũ
  ['Ninh Bình', 'Thanh Hóa'],
  // ── Miền Trung ──
  ['Thanh Hóa', 'Nghệ An'],
  ['Nghệ An', 'Hà Tĩnh'],
  ['Hà Tĩnh', 'Quảng Trị'], // Quảng Bình cũ
  ['Quảng Trị', 'Huế'],
  ['Huế', 'Đà Nẵng'], // Đà Nẵng, Quảng Nam cũ
  ['Đà Nẵng', 'Quảng Ngãi'], // Quảng Nam – Quảng Ngãi, Kon Tum
  ['Quảng Ngãi', 'Gia Lai'], // Quảng Ngãi, Kon Tum – Gia Lai, Bình Định
  ['Gia Lai', 'Đắk Lắk'], // Gia Lai, Bình Định – Đắk Lắk, Phú Yên
  ['Đắk Lắk', 'Khánh Hòa'],
  ['Đắk Lắk', 'Lâm Đồng'], // Đắk Lắk – Lâm Đồng, Đắk Nông
  ['Khánh Hòa', 'Lâm Đồng'], // Khánh Hòa, Ninh Thuận – Lâm Đồng, Bình Thuận
  // ── Trung ↔ Nam ──
  ['Lâm Đồng', 'Đồng Nai'], // Lâm Đồng, Bình Thuận, Đắk Nông – Đồng Nai, Bình Phước
  ['Lâm Đồng', 'Hồ Chí Minh'], // Bình Thuận – Bà Rịa-Vũng Tàu
  // ── Miền Nam ──
  ['Hồ Chí Minh', 'Đồng Nai'],
  ['Hồ Chí Minh', 'Tây Ninh'], // Hồ Chí Minh, Bình Dương – Tây Ninh, Long An
  ['Hồ Chí Minh', 'Đồng Tháp'], // Hồ Chí Minh – Tiền Giang
  ['Đồng Nai', 'Tây Ninh'], // Bình Phước – Tây Ninh
  ['Tây Ninh', 'Đồng Tháp'], // Long An – Đồng Tháp, Tiền Giang
  ['Đồng Tháp', 'Vĩnh Long'], // Tiền Giang, Đồng Tháp – Vĩnh Long, Bến Tre
  ['Đồng Tháp', 'Cần Thơ'],
  ['Đồng Tháp', 'An Giang'],
  ['Vĩnh Long', 'Cần Thơ'], // Vĩnh Long, Trà Vinh – Cần Thơ, Hậu Giang, Sóc Trăng
  ['Cần Thơ', 'An Giang'], // Cần Thơ, Hậu Giang – An Giang, Kiên Giang
  ['Cần Thơ', 'Cà Mau'], // Hậu Giang, Sóc Trăng – Bạc Liêu
  ['An Giang', 'Cà Mau'], // Kiên Giang – Cà Mau, Bạc Liêu
]

const NEIGHBOR_KEYS = new Set(PROVINCE_NEIGHBORS.flatMap(([a, b]) => [`${a}|${b}`, `${b}|${a}`]))

/** Hai tỉnh (tên bất kỳ, cũ hoặc mới) có giáp ranh nhau không — theo địa giới 34 tỉnh. */
export function areNeighborProvinces(a?: string | null, b?: string | null): boolean {
  const pa = toProvince34(a)
  const pb = toProvince34(b)
  return !!pa && !!pb && pa !== pb && NEIGHBOR_KEYS.has(`${pa}|${pb}`)
}

/** Tỉnh cũ (63 tỉnh) → tỉnh mới (34 tỉnh). Tỉnh giữ nguyên tên không cần liệt kê. */
export const MERGED_INTO: Record<string, string> = {
  'Hà Giang': 'Tuyên Quang',
  'Yên Bái': 'Lào Cai',
  'Bắc Kạn': 'Thái Nguyên',
  'Vĩnh Phúc': 'Phú Thọ',
  'Hòa Bình': 'Phú Thọ',
  'Hoà Bình': 'Phú Thọ',
  'Bắc Giang': 'Bắc Ninh',
  'Hải Dương': 'Hải Phòng',
  'Thái Bình': 'Hưng Yên',
  'Hà Nam': 'Ninh Bình',
  'Nam Định': 'Ninh Bình',
  'Quảng Bình': 'Quảng Trị',
  'Thừa Thiên Huế': 'Huế',
  'Quảng Nam': 'Đà Nẵng',
  'Kon Tum': 'Quảng Ngãi',
  'Bình Định': 'Gia Lai',
  'Phú Yên': 'Đắk Lắk',
  'Ninh Thuận': 'Khánh Hòa',
  'Đắk Nông': 'Lâm Đồng',
  'Bình Thuận': 'Lâm Đồng',
  'Bình Dương': 'Hồ Chí Minh',
  'Bà Rịa - Vũng Tàu': 'Hồ Chí Minh',
  'Bà Rịa-Vũng Tàu': 'Hồ Chí Minh',
  'Bình Phước': 'Đồng Nai',
  'Long An': 'Tây Ninh',
  'Tiền Giang': 'Đồng Tháp',
  'Bến Tre': 'Vĩnh Long',
  'Trà Vinh': 'Vĩnh Long',
  'Sóc Trăng': 'Cần Thơ',
  'Hậu Giang': 'Cần Thơ',
  'Kiên Giang': 'An Giang',
  'Bạc Liêu': 'Cà Mau',
}

const norm = (s: string) => removeVietnameseAccents(s).replace(/\s+/g, ' ').trim()

const NEW_BY_NORM = new Map(Object.keys(PROVINCE_REGION).map((p) => [norm(p), p]))
const MERGED_BY_NORM = new Map(Object.entries(MERGED_INTO).map(([oldName, newName]) => [norm(oldName), newName]))

/**
 * Quy một tên tỉnh bất kỳ (cũ hoặc mới, có/không dấu, có "Tỉnh"/"TP.") về tên
 * tỉnh mới dạng rút gọn, ví dụ "Tỉnh Bình Dương" → "Hồ Chí Minh". Không nhận ra
 * → null.
 */
export function toProvince34(name?: string | null): string | null {
  if (!name || !name.trim()) return null
  // Địa chỉ đầy đủ ("12 Nguyễn Huệ, Quận 1, TP. Hồ Chí Minh"): tỉnh luôn ở cuối,
  // nên dò từng đoạn từ phải sang trái. Dò cả chuỗi một lần thì "Huệ" trong tên
  // đường khớp nhầm tỉnh Huế.
  const parts = name.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean)
  for (let i = parts.length - 1; i >= 0; i--) {
    const hit = matchProvince34(parts[i]!)
    if (hit) return hit
  }
  return null
}

function matchProvince34(part: string): string | null {
  const direct = norm(cleanAdminPrefix(part))
  const exact = MERGED_BY_NORM.get(direct) ?? NEW_BY_NORM.get(direct)
  if (exact) return exact
  const matched = findProvince(part)
  if (!matched) return null
  const short = norm(cleanAdminPrefix(matched))
  // "Thừa Thiên Huế" / "Thành phố Huế" đều về "Huế"
  const key = short === 'thua thien hue' ? norm('Thừa Thiên Huế') : short
  return MERGED_BY_NORM.get(key) ?? NEW_BY_NORM.get(key) ?? null
}

/** Tên tỉnh mới dạng rút gọn; không nhận ra thì trả chuỗi rút gọn của đầu vào. */
export function canonicalizeProvince(name?: string | null): string {
  if (!name || !name.trim()) return ''
  return toProvince34(name) ?? cleanAdminPrefix(findProvince(name) || name).trim()
}

export function getRegionForProvince(provinceName?: string | null): VietnamRegion | null {
  const p = toProvince34(provinceName)
  return p ? PROVINCE_REGION[p] ?? null : null
}

/**
 * Vùng giao hàng giữa tỉnh gửi (kho shop) và tỉnh nhận. Trả `null` khi không
 * nhận ra một trong hai tỉnh — nơi gọi quyết định xử lý, không đoán.
 */
export function resolveShippingRoute(
  senderProvince?: string | null,
  recipientProvince?: string | null,
): ShippingRoute | null {
  const s = toProvince34(senderProvince)
  const r = toProvince34(recipientProvince)
  if (!s || !r) return null
  if (s === r) return 'noi_tinh'
  if (PROVINCE_REGION[s] !== PROVINCE_REGION[r]) return 'lien_mien'
  return NEIGHBOR_KEYS.has(`${s}|${r}`) ? 'lien_tinh' : 'noi_mien'
}
